import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { LanguageSwitcher } from '../components/common/LanguageSwitcher';
import { Logo } from '../components/common/Logo';

// URL документов (публичные файлы в S3)
const DOCUMENTS = {
    userAgreement: 'https://pf-project.object.pscloud.io/ACCORD-aiser.pdf',
    privacyPolicy: 'https://pf-project.object.pscloud.io/Privacy_policy-aiser.pdf',
    publicOffer: 'https://pf-project.object.pscloud.io/PUBLIC_OFFER.pdf',
};

// Иконка PDF
const PdfIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
    <div className={`relative ${className}`}>
        <div className="w-14 h-16 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200 shadow-sm">
            <div className="relative">
                {/* Иконка документа */}
                <DocumentTextIcon className="w-8 h-8 text-gray-400" />
                {/* Метка PDF */}
                <div className="absolute -bottom-1 -right-2 bg-red-500 text-white text-[8px] font-bold px-1 py-0.5 rounded">
                    PDF
                </div>
            </div>
        </div>
    </div>
);

interface DocumentCardProps {
    title: string;
    description: string;
    url: string;
    buttonText: string;
}

const DocumentCard: React.FC<DocumentCardProps> = ({ title, description, url, buttonText }) => {
    const handleClick = () => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="bg-white rounded-2xl p-6 flex items-center gap-5 shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100">
            <PdfIcon />
            <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                    {description}
                </p>
            </div>
            <button
                onClick={handleClick}
                className="flex-shrink-0 bg-amber-500 hover:bg-amber-600 text-white font-medium px-6 py-3 rounded-full transition-colors duration-200 shadow-sm hover:shadow-md"
            >
                {buttonText}
            </button>
        </div>
    );
};

export const DocumentsPublicPage: React.FC = () => {
    const { t } = useTranslation(['auth', 'common']);

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Header */}
            <header className="bg-white shadow-sm">
                <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Link
                        to="/auth"
                        className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <ArrowLeftIcon className="w-5 h-5 mr-2" />
                        <span className="text-sm font-medium">{t('auth:documents.backToLogin')}</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center overflow-hidden p-1">
                            <Logo size="sm" className="w-full h-full rounded-full" />
                        </div>
                        <LanguageSwitcher />
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-4 py-12">
                {/* Title Section */}
                <div className="mb-10">
                    <h1 className="text-3xl font-bold text-gray-900 mb-3">
                        {t('auth:documents.pageTitle')}
                    </h1>
                    <p className="text-gray-600 text-lg">
                        {t('auth:documents.pageSubtitle')}
                    </p>
                </div>

                {/* Documents List */}
                <div className="space-y-4">
                    <DocumentCard
                        title={t('auth:documents.userAgreement')}
                        description={t('auth:documents.userAgreementDesc')}
                        url={DOCUMENTS.userAgreement}
                        buttonText={t('auth:documents.viewDocument')}
                    />
                    <DocumentCard
                        title={t('auth:documents.privacyPolicy')}
                        description={t('auth:documents.privacyPolicyDesc')}
                        url={DOCUMENTS.privacyPolicy}
                        buttonText={t('auth:documents.viewDocument')}
                    />
                    <DocumentCard
                        title={t('auth:documents.publicOffer')}
                        description={t('auth:documents.publicOfferDesc')}
                        url={DOCUMENTS.publicOffer}
                        buttonText={t('auth:documents.viewDocument')}
                    />
                </div>

                {/* Footer Note */}
                <div className="mt-10 text-center text-gray-500 text-sm">
                    <p>© {new Date().getFullYear()} AISER. {t('common:appName')}</p>
                </div>
            </main>
        </div>
    );
};

export default DocumentsPublicPage;

